<template>
  <div class="table-grid">
    <div v-if="useSearch" class="d-flex flex-row justify-space-between align-center mb-3">
      <slot name="nameTable">
        <h1 class="section-header__title">
          {{ nameTable }}
        </h1>
      </slot>
      <div class="d-flex flex-row align-start">
        <v-icon
            size="20"
            color="info"
            class="mt-2 mr-2"
        >
          mdi-magnify
        </v-icon>
        <v-text-field
            :value="search"
            color="info"
            outlined
            :label="labelSearch"
            class="search-field"
            dense
            :loading="searching"
            @change="changeSearch($event)"
            style="min-width: 500px"
        />
      </div>
    </div>
    <div
        v-if="isGridTable"
        class="table-grid__container"
        :style="`grid-template-columns : repeat(${numCols}, 1fr); max-height: ${resizedMaxHeight};`"
    >
      <!--       header -->
      <grid-table-header-row :headers="headers" :num-cols="numCols" :color="loaderColor" :have-vuetify="haveVuetify">
        <slot v-for="(_, name) in $slots" :name="name" :slot="name"/>
        <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
          <slot :name="name" v-bind="slotData"/>
        </template>
      </grid-table-header-row>
      <!--        body-->
      <template v-if="loadingData || loading">
        <div class="d-flex flex-row align-center" :style="`grid-column: span ${numCols}; min-height: 200px;`">
          <slot name="loading">
            <v-progress-linear v-if="haveVuetify" indeterminate :color="loaderColor"/>
            <template v-else>
              Loading...
            </template>
          </slot>
        </div>
      </template>
      <grid-table-empty-data :num-cols="numCols" v-else-if="showingServerData.length === 0"/>
      <template v-else>
        <grid-table-body-row
            v-for="(item, j) in showingServerData"
            :row="item"
            :key="(item.id != null ? item.id  : j)"
            :num-cols="numCols"
            :paint-background="paintBackground"
            :headers="headersForRows"
            @row-action="rowAction($event,item)"
        >
          <slot v-for="(_, name) in $slots" :name="name" :slot="name"/>
          <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
            <slot v-if="useSlots" :name="name" v-bind="slotData"/>
          </template>
        </grid-table-body-row>
      </template>
    </div>
    <table v-else
           class="table-on-td"
           :style="`max-height: ${resizedMaxHeight};`"
    >
      <thead>
      <tr>
        <th
            v-for="(item, i) in headers" :key="i"
            :class="item.headerClass"
            :colspan="item.cols"
            :rowspan="item.rows"
            :ref="'table-header__cell-'+item.dataField"
            :style="`${item.headerStyles}`"
        >
          <slot :name="'header-'+item.dataField" :item="item">
            <span v-html="item.text"></span>
            <span
                class="d-flex align-center justify-center"
                v-if="item.sortable"
            >
                <template v-if="sort.header === item.dataField">

                </template>
                <template v-else>

                </template>
          </span>
          </slot>
        </th>
      </tr>
      </thead>
      <tbody>
      <template v-if="loadingData || loading">
        <tr>
          <td class="d-flex flex-row align-center" :colspan="numCols"
              :style="` min-height: 200px;`">
            <slot name="loading">
              <v-progress-linear v-if="haveVuetify" indeterminate :color="loaderColor"/>
              <template v-else>
                Loading...
              </template>
            </slot>
          </td>
        </tr>
      </template>
      <template v-else>
        <tr
            v-for="(row, j) in showingServerData"
            class=""
            style=""
            @click="rowAction($event,row)"
            :key="(row.id != null ? row.id  : j)"
            @row-action="rowAction($event,row)"
        >
          <td
              v-for="cell in headersForRows"
              class=""
              :key="cell.dataField"
              :colspan="cell.cols"
              :rowspan="cell.rows"
              :class="[cell.class, painCell(row, cell)]"
              :style="`${typeof cell.styles === 'function' ? cell.styles(row) : cell.styles};`"
          >
            <slot v-if="useSlots" :name="cell.dataField" :row="row" :id="row.id != null ? row.id : Math.random()"
                  :cell="cell">
              <span v-html="cellData(row, cell)"></span>
            </slot>
            <span v-else v-html="cellData(row, cell)"></span>
          </td>
        </tr>
      </template>
      </tbody>
    </table>
    <div v-if="haveVuetify" class="mt-3 d-flex flex-row justify-center align-center">
      <v-autocomplete
          class="pagination-table"
          v-model="userPages"
          :items="pagesPaginate"
          solo
          dense
          filled
          height="25"
          rounded
          style="max-width: 150px"
      ></v-autocomplete>
      <v-pagination
          v-model="page"
          :length="pagesVuetify"
          :total-visible="7"
      >
      </v-pagination>

    </div>
  </div>
</template>

<script>


import GridTableHeaderRow from "./GridTableHeaderRow";
import GridTableEmptyData from "./GridTableEmptyData";
import GridTableBodyRow from "./GridTableBodyRow";

export default {
  name: "GridTableFull",
  components: {
    GridTableBodyRow, GridTableEmptyData, GridTableHeaderRow,
  },
  props: {
    headers: {
      type: [Array],
      required: true,
      default: () => []
    },
    typeTable: {
      type: String,
      default: 'grid',
      validator: (v) => {
        return ['grid', 'table'].includes(v)
      }
    },
    numCols: {
      type: [Number, String],
      required: true,
      default: 0
    },
    headersForRows: {
      type: [Array],
      required: true,
      default: () => []
    },
    maxTableHeight: {
      type: [String, Number],
      default: (window.screen.availHeight - 370 + 'px') ?? '550px',
    },
    loadingData: {
      type: Boolean,
      default: false
    },
    useSlots: {
      type: Boolean,
      default: true
    },
    dataRows: {
      type: Array,
      default: () => []
    },
    paintBackground: {
      type: [Function],
      default: () => {
      }
    },
    importsComponents: {
      type: [Array, Object]
    },
    loaderColor: {
      type: String,
      default: '#00599b'
    },
    labelSearch: {
      type: String,
      default: 'Поиск'
    },
    nameTable: {
      type: String,
      default: 'Название таблицы'
    },
    useVirtualScroller: {
      type: Boolean,
      default: false
    },
    useSearch: {
      type: Boolean,
      default: true
    },
    userPerPagesDefault: {
      type: Number,
      default: 25
    },
    useOnlyPerPagesDefault: {
      type: Boolean,
      default: false
    },
    sizeElementForVirtualScroller: {
      type: [String, Number],
      default: 40
    },
    dataFieldsForSearch: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    sizeElementForVirtualScrollerValidated() {
      return Number.parseInt(this.sizeElementForVirtualScroller)
    },
    maxTableHeightForScroller() {
      return window.screen.availHeight - 375 + 'px'
    },
    rowFields() {
      return this.headersForRows.map(el => ('item.' + el.dataField))
    },
    isGridTable() {
      return this.typeTable === 'grid'
    },
    haveVuetify() {
      return !!this.$vuetify
    },
    lengthData() {
      return this.dataRows.length
    },
    // filterData(){
    //   return this.dataRows
    //       .filter(el => {
    //             if (!this.search)
    //               return true;
    //             let include = false
    //             this.dataFieldsForSearch.forEach(el2 => {
    //               if (include)
    //                 return;
    //               include = String(el[el2]).toLowerCase().search(this.search.toLowerCase()) !== -1;
    //             })
    //             return include
    //           }
    //       )
    // },
    showingServerData() {
      return this.filterData
          .slice((this.page - 1) * this.userPages, (this.page) * this.userPages)
    },
    pagesVuetify() {
      return Math.ceil(this.filterData.length / this.userPages);
    },
  },
  data() {
    return {
      localHeaders: this.headers,
      loading: false,
      searching: false,
      search: null,
      reseizing: false,
      resizedMaxHeight: null,
      sort: {header: null, type: 'asc'},
      /*pagination*/
      page: 1,
      filterData: [],
      userPages: this.userPerPagesDefault,
      pagesPaginate: [
        {value: 25, text: 25},
        {value: 50, text: 50},
        {value: 75, text: 75},
        {value: 100, text: 100},
      ]
    }
  },
  watch: {
    async headers(newValue) {
      // console.log(this.$refs['grid-table'])
      // await this.$refs['grid-table'].scrollIntoView({block: "start", inline: "nearest"});
      this.loading = true;
      this.localHeaders = newValue;
      // this.initClass();
      // await this.setTopPositions();
      this.loading = false;
    },
    lengthData(newValue) {
      if (this.useOnlyPerPagesDefault)
        return
      if (newValue <= 25)
        this.userPages = 25;
      else if (newValue <= 50)
        this.userPages = 50;
      else if (newValue <= 75)
        this.userPages = 75;
      else
        this.userPages = 100;
    },
    dataRows: {
      deep: true,
      handler(v) {
        // console.log('dataRows', this.search)
        this.filterData = !this.search ? v : v.filter(el => {
              if (!this.search)
                return true;
              let include = false
              this.dataFieldsForSearch.forEach(el2 => {
                if (include)
                  return;
                include = String(el[el2]).toLowerCase().search(this.search.toLowerCase()) !== -1;
              })
              return include
            }
        )
      }
    },
    search(newValue) {
      console.log('search', newValue)
      if (!newValue) {
        this.filterData = this.dataRows;
        return;
      }
      this.searching = true;
      this.filterData = this.dataRows.filter(el => {
            if (!newValue)
              return true;
            let include = false
            this.dataFieldsForSearch.forEach(el2 => {
              if (include)
                return;
              include = String(el[el2]).toLowerCase().search(newValue.toLowerCase()) !== -1;
            })
            return include
          }
      )
      setTimeout(() => {
        this.searching = false;
      }, 300)


    }
  },
  created() {
    this.filterData = !this.search ? this.dataRows : this.dataRows.filter(el => {
          if (!this.search)
            return true;
          let include = false
          this.dataFieldsForSearch.forEach(el2 => {
            if (include)
              return;
            include = String(el[el2]).toLowerCase().search(this.search.toLowerCase()) !== -1;
          })
          return include
        }
    )
  },
  mounted() {
    // this.setTopPositions();
    // this.initClass();
    this.resizeMaxHeight();
    window.addEventListener('resize', this.resizeMaxHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeMaxHeight)
    // window.removeEventListener('resize', this.setTopPositions)
  },
  methods: {
    changeSearch(newValue) {
      this.search = newValue
    },
    resizeMaxHeight() {
      if (this.reseizing) return;
      this.reseizing = true;
      const height = (window.screen.availHeight - 350) + 'px';
      if (this.maxTableHeight > height)
        this.resizedMaxHeight = height;
      else
        this.resizedMaxHeight = this.maxTableHeight;
      this.reseizing = false;
    },
    rowAction(e, row) {
      this.$emit('row-action', {e: e, row: row})
    },
    cellData(row, cell) {
      return cell.template ? cell.template(row) : row[cell.dataField]
    },
    calcStylesForRowCell(row, cell) {
      return `${typeof cell.styles === 'function' ? cell.styles(row) : cell.styles};` + `grid-column: span ${cell.colsDataCalc ? cell.colsDataCalc(row, cell) : cell.cols}; grid-row: span ${cell.rowsDataCalc ? cell.rowsDataCalc(row) : cell.rowsData};`
    },
    // eslint-disable-next-line no-unused-vars
    painCell(row, cell) {
      if (this.paintBackground(row))
        return this.paintBackground(row)();
    },
  },
}
</script>

<style scoped>
</style>